import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AiConductingBanner } from '../features/conversations/components/AiConductingBanner';
import { ClaimBanner } from '../features/conversations/components/ClaimBanner';
import { CommitmentBanner } from '../features/conversations/components/CommitmentBanner';
import { Composer } from '../features/conversations/components/Composer';
import { ConsultaDeEpisodioBanner } from '../features/conversations/components/ConsultaDeEpisodioBanner';
import { ConversationHeader } from '../features/conversations/components/ConversationHeader';
import { EpisodeParallelBanner } from '../features/conversations/components/EpisodeParallelBanner';
import { rotuloDoEpisodio } from '../features/conversations/components/EpisodeRail';
import { ForbiddenConversationNotice } from '../features/conversations/components/ForbiddenConversationNotice';
import { HybridPanel } from '../features/conversations/components/HybridPanel/HybridPanel';
import { MessageList } from '../features/conversations/components/MessageList';
import { PlaybookTransitionIndicator } from '../features/conversations/components/PlaybookTransitionIndicator';
import { PlaybookTransitionPendingBanner } from '../features/conversations/components/PlaybookTransitionPendingBanner';
import { ReopenedBanner } from '../features/conversations/components/ReopenedBanner';
import {
  persistFioChoice,
  readFioChoice,
  resolveFioDefault,
} from '../features/conversations/fio-default';
import { conducaoIaVisivel, modoConsulta } from '../features/conversations/format';
import { useClaimConversation } from '../features/conversations/hooks/use-claim-conversation';
import { useConversationEvents } from '../features/conversations/hooks/use-conversation-events';
import { useConversationViewing } from '../features/conversations/hooks/use-conversation-viewing';
import { usePendencies } from '../features/conversations/hooks/use-panel-fixtures';
import { useSendMessage } from '../features/conversations/hooks/use-send-message';
import { usePersonConversations } from '../features/persons/use-persons';
import { useCapability } from '../hooks/use-capability';
import { useConversation } from '../hooks/use-conversation';
import { useMessages } from '../hooks/use-messages';
import { ApiError } from '../lib/api-client';
import { useAuthStore } from '../stores/auth-store';

import type { ConversationDetail } from '../lib/types';

/**
 * Página de Conversa em curso — composição Brief 2A V2.3.
 *
 * Layout:
 *   ┌────────────────────────────────────────┬──────────────────┐
 *   │   ConversationHeader (Tier 1 ~52px)    │                  │
 *   ├────────────────────────────────────────┤   HybridPanel    │
 *   │                                        │   (sticky        │
 *   │   MessageList                          │    LivingSummary │
 *   │                                        │    + 4 abas)     │
 *   │                                        │                  │
 *   ├────────────────────────────────────────┤                  │
 *   │   Composer Enxuto v2                   │                  │
 *   └────────────────────────────────────────┴──────────────────┘
 *
 * Em tablet (`<lg`), HybridPanel hidden (revisitar na F12 / refinos).
 */
export function ConversationViewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: conversation, isLoading, isError, error } = useConversation(id);

  /**
   * Spec 2026-08-14 §5.1 — as pendências que nasceram de uma fala deste fio, para a
   * linha de captura sob a mensagem que as originou.
   *
   * Roda ANTES dos early returns (regra dos hooks) e por isso recebe um placeholder
   * de id vazio enquanto a conversa carrega. Não é gambiarra: `usePendencies` já se
   * gateia por `conversation.id.length > 0`, então com id vazio o `enabled` é falso e
   * NENHUMA query dispara — o mesmo mecanismo que ele usa para não buscar em tenant
   * sem a capability.
   *
   * Zero request adicional: o painel direito busca a mesma lista com a mesma
   * queryKey, e o React Query serve as duas do mesmo cache.
   */
  const { items: panelPendencies } = usePendencies(
    conversation ?? ({ id: '' } as ConversationDetail),
  );
  // B2 (spec 2026-08-12 §3.4) — transcript contínuo por pessoa: o operador
  // liga; o servidor decide (flag ∧ cap ∧ regras duras); a UI declara quando
  // degradou. Oferta gateada por capability + pessoa presente.
  const canPersonTranscript = useCapability('person-transcript-read');
  // Alvo D round2 — o fio nasce LIGADO para quem tem a cap; «ver só esta
  // conversa» é opt-out persistido por operador (helper puro pinado por spec).
  const [personScopeActive, setPersonScopeActive] = useState(() =>
    resolveFioDefault(canPersonTranscript, readFioChoice()),
  );
  /**
   * MODO CONSULTA (decisão do TL, 13/Ago/2026).
   *
   * Consultar um episódio antigo é NAVEGAR até ele — a página de conversa já
   * sabe se comportar em estado terminal (composer em leitura por
   * `isTerminalStatus`, cabeçalho próprio). Não existe estado híbrido «fio de
   * uma pessoa + painel de um episódio + composer de um terceiro»: seria um
   * estado que o app não tem e que precisaria ser explicado ao operador.
   *
   * O parâmetro carrega DUAS informações numa só: que se chegou aqui para
   * consultar (⇒ fio desligado, trilha escondida — é o sinal visual de que isto
   * não é conversa operacional) e PARA ONDE voltar. Vive na URL para sobreviver
   * a um refresh.
   */
  const [searchParams] = useSearchParams();
  const voltarAoFio = searchParams.get('voltarAoFio');
  // A-41 — veio pelo aviso «Orientação da supervisão → Abrir»: abre o painel de orientações.
  const abrirSupervisao = searchParams.get('supervisao') === '1';
  /**
   * O parâmetro diz COMO se chegou; o status diz O QUE a conversa é. Consulta
   * exige as duas coisas.
   *
   * A primeira versão exigia só o parâmetro, e isso produziu o defeito que o TL
   * mediu na tela: estando num episódio antigo e clicando na conversa VIVA da
   * trilha, a navegação levava o parâmetro junto — e a conversa ativa abria
   * rotulada «somente leitura», com a trilha escondida. Consulta virava função
   * de por onde se passou, não do que se está olhando.
   *
   * Com o status na conta, a regra se corrige sozinha: navegar para uma conversa
   * viva nunca entra em consulta, venha o parâmetro ou não.
   */
  const emConsulta = modoConsulta(voltarAoFio, conversation?.status);
  // O fio não se desliga por preferência aqui — ele é suprimido pelo modo. A
  // escolha persistida do operador fica intacta para quando ele voltar.
  const fioLigado = personScopeActive && !emConsulta;
  // Alvo D — episódios + total real (endpoint da ficha, cache compartilhado
  // com painel/grupo; fetch GATED por cap + pessoa + fio pedido).
  const scopePersonId = conversation?.personId ?? conversation?.person?.id ?? undefined;
  const { data: personHistory } = usePersonConversations(
    scopePersonId,
    // Em consulta o fio está desligado, mas a ficha segue valendo: é dela que
    // sai o rótulo da barra de volta — e tem que ser o MESMO rótulo da oferta
    // que trouxe o operador até aqui, senão ele não reconhece onde está.
    canPersonTranscript && scopePersonId !== undefined && (fioLigado || emConsulta),
  );
  // F.5 M1 — o fio só faz sentido com HISTÓRICO: sem 2+ conversas da pessoa,
  // o escopo segue o clássico (nada de banner «1 conversas» nem query
  // person-scope para o caso majoritário). Enquanto o total não chega, o
  // escopo clássico vale — a promoção ao fio é reativa ao cache da ficha.
  const fioEligible = (personHistory?.meta.totalCount ?? 0) > 1;
  // O episódio que está sendo consultado, dentro da ficha da pessoa. Serve só
  // para o rótulo da barra de volta — e é `undefined` enquanto a ficha não
  // chega, caso em que a barra diz «uma conversa anterior» em vez de mentir.
  const episodioConsultado = emConsulta ? personHistory?.data.find((e) => e.id === id) : undefined;
  const {
    messages,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: msgsLoading,
    personScopeApplied,
  } = useMessages(id, fioLigado && fioEligible);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { retry } = useSendMessage(id, currentUserId);
  const { claim, isPending: isClaiming } = useClaimConversation(id);
  const navigate = useNavigate();

  // Real-time canonical — WS subscribe MESSAGE_RECEIVED + CONVERSATION_CREATED
  // + STATUS_CHANGED + DELIVERY_UPDATED → invalidate cache canonical.
  //
  // **Chamada do hook preservada mesmo sem consumer do output** — `useConversationEvents`
  // tem side-effects no useEffect (WS subscribe + invalidate cache) além do retorno
  // explícito. Lição operacional canonical da PR #105: remover esta chamada por
  // engano quebrou real-time canonical (regressão da PR #104).
  //
  // **NewMessageIndicator removido** (era redundante com `JumpToBottomButton`
  // canonical em MessageList:151, que faz scroll-to-bottom + unreadCount badge).
  // Atendente já tem feedback visual canonical via JumpToBottomButton no canto
  // inferior direito da área de mensagens — sem sobreposição ao Composer.
  // Fatia 11.2 — consome `lastTransition` para banner discreto sticky abaixo
  // do header quando transition silenciosa acontece em modo H_c.
  const { lastTransition, dismissTransition } = useConversationEvents(id);

  // Frente engine-led S7 (Condução Governada) — sinal de foco por-conversa: avisa
  // o Engine que este operador abriu a conversa (viewing:true no montar +
  // heartbeat; false no desmontar). Gated (cap `conversation-viewing-awareness`):
  // inerte quando OFF. Alimenta o alerta de cliente-em-espera (Peça 3).
  useConversationViewing(id);

  /**
   * Item 88 — 403 em URL direta (Carlos navega `/conversations/<ana-id>`):
   * backend nega corretamente, ToastContainer já notifica via onForbidden
   * (setup-api-client). Aqui apenas redirecionamos para listagem para não
   * deixar a UI presa numa página vazia. Sessão preservada (401 segue
   * caminho próprio em RequireAuth + onUnauthorized).
   */
  // A-11 (noturna 04/Set/2026): o redirect mudo para `/` levava ao Painel pessoal
  // sem explicar. Agora o 403 vira aviso com a razão do backend (`not-assigned`…).
  const isForbidden = isError && error instanceof ApiError && error.status === 403;
  if (isForbidden) {
    return <ForbiddenConversationNotice reason={error.body.reason} />;
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-text-secondary text-sm">
        Carregando conversa…
      </div>
    );
  }
  if (isError || conversation === undefined) {
    return (
      <div className="flex h-full items-center justify-center text-red-600 text-sm">
        Não foi possível carregar a conversa.
      </div>
    );
  }

  // useMessages monta a partir do endpoint /messages (paginado). Antes
  // do primeiro fetch ele retorna vazio — usamos fallback no payload
  // inicial do detalhe (já vem com últimas N mensagens).
  const renderedMessages = messages.length > 0 ? messages : conversation.messages.slice().reverse();

  // Fatia 11.1 — modo H_c (IA dirigindo): Composer é substituído por
  // AIConductingBanner com botão Retomar (⌘R). Visual canonical Brief 2A V2.3 §C.1.
  const operationModeRaw = (conversation.operationMode as { current?: string } | null)?.current;
  // `isTerminalStatus` entra aqui porque o banner de condução oferece AÇÃO
  // («clique para assumir»). Numa conversa encerrada não há fila a segurar nem
  // o que assumir — o TL viu «IA está segurando a fila» num episódio antigo. O
  // ramo de baixo já se protege sozinho: `Composer` lê o mesmo `isTerminalStatus`.
  const isHCMode = conducaoIaVisivel(operationModeRaw, conversation.status);

  return (
    <div className="flex h-full">
      <div className="relative flex flex-col flex-1 min-w-0">
        <ConversationHeader conversation={conversation} />
        {emConsulta && (
          <ConsultaDeEpisodioBanner
            rotuloDoEpisodio={
              episodioConsultado === undefined ? null : rotuloDoEpisodio(episodioConsultado)
            }
            nomeDaPessoa={conversation.person?.displayName ?? null}
            onVoltar={() => {
              // Voltar devolve TUDO junto — fio, trilha e painel na conversa
              // aberta. Reafirmar a escolha do fio é o que garante que a trilha
              // reapareça mesmo se o operador tinha o opt-out gravado antes.
              persistFioChoice(true);
              setPersonScopeActive(true);
              navigate(`/atendimento/conversation/${voltarAoFio ?? ''}`);
            }}
          />
        )}
        {/* S2 PR-b (Corrente de Compromissos) — pouso com propósito: 1ª coisa
            visível abaixo do header, acima de qualquer outro banner/histórico.
            Self-contido (gateia cap `conversation-commitments` + fetch por dentro). */}
        <CommitmentBanner conversation={conversation} />
        <PlaybookTransitionIndicator transition={lastTransition} onDismiss={dismissTransition} />
        {/* S7 PR3 (S6 Condução Governada, Q-J6) — indicador DECISÓRIO (só some
            quando o atendente confirma/rejeita, nunca por timeout); self-contido
            (gateia cap+fetch por dentro). Gated OFF por padrão (S6 flag/caps). */}
        <PlaybookTransitionPendingBanner conversationId={conversation.id} />
        <MessageList
          messages={renderedMessages}
          hasMore={hasMore}
          isFetchingMore={isFetchingNextPage}
          isLoading={msgsLoading && messages.length === 0 && conversation.messages.length === 0}
          onLoadOlder={fetchNextPage}
          onRetry={(optimisticId) => {
            void retry(optimisticId);
          }}
          currentConversationId={conversation.id}
          capturedPendencies={panelPendencies}
          {
            // `personScope` não vai em consulta: sem isto, a lista renderizava
            // «Ver o fio completo desta pessoa» DENTRO da rolagem ao mesmo tempo
            // que a barra de cima oferecia «voltar pro fio» — duas portas para a
            // mesma saída, e uma delas exigindo rolar até o topo, que é exatamente
            // o que a barra veio resolver (achado do TL, 13/Ago).
            ...(!emConsulta &&
              canPersonTranscript &&
              fioEligible &&
              (conversation.personId ?? conversation.person?.id) != null && {
                personScope: {
                  active: fioLigado,
                  applied: personScopeApplied,
                  // Alvo D — a escolha do operador persiste (opt-out do default).
                  onToggle: () =>
                    setPersonScopeActive((v) => {
                      persistFioChoice(!v);
                      return !v;
                    }),
                  ...(personHistory?.meta.totalCount !== undefined && {
                    totalCount: personHistory.meta.totalCount,
                  }),
                  ...(personHistory !== undefined && { episodes: personHistory.data }),
                  onOpenEpisode: (conversationId: string) => {
                    // A âncora da volta é a conversa de ONDE se partiu — e se já
                    // se está em consulta, a original, não a que se está lendo.
                    const ancora = voltarAoFio ?? conversation.id;
                    navigate(
                      `/atendimento/conversation/${conversationId}?voltarAoFio=${encodeURIComponent(ancora)}`,
                    );
                  },
                },
              })
          }
        />
        {/* ADR-155 Fase 4 (§3.2) — independente do modo (H_a/H_c): aviso de
            conversas paralelas apontando o mesmo episódio. */}
        <EpisodeParallelBanner count={conversation.episodeParallelConversations ?? 0} />
        {/* Spec fila §D6 parte 3 — reabertura importa nos DOIS modos (H_c e H_a);
            posicionado ANTES do branch de modo para que ambos os ramos a renderizem. */}
        <ReopenedBanner reopenedAt={conversation.reopenedAt} status={conversation.status} />
        {isHCMode ? (
          <AiConductingBanner
            conversationId={conversation.id}
            persistedEscalationState={conversation.escalationState ?? null}
            escalationReason={conversation.escalationReason ?? null}
            delegation={conversation.delegation ?? null}
            assignedToUserId={conversation.assignedToUserId}
          />
        ) : (
          <>
            <ClaimBanner
              assignedToUserId={conversation.assignedToUserId}
              status={conversation.status}
              onClaim={() => void claim()}
              isPending={isClaiming}
            />
            <Composer conversation={conversation} />
          </>
        )}
      </div>
      <div className="hidden lg:flex">
        <HybridPanel conversation={conversation} openSupervision={abrirSupervisao} />
      </div>
    </div>
  );
}
